import { useNavigate } from 'react-router-dom';
import { Card, Form } from '@bancobarigui/barihaus';

import { InspectionForm } from './InspectionForm';
import { TableCard } from '@/components/TableCard';
import { ItemModal } from '@/components/ItemModal';
import { ProponentModal } from '@/components/ProponentModal';
import { AttachmentModal } from '@/components/AttachmentModal';
import { RegisterFormButtons } from '@/components/RegisterFormButtons';
import { ProtectedRouteContainer } from '@/components/ProtectedRouteContainer';
import { PageHeader } from '@/components/PageHeader';
import { DuplicateLocationModal } from '@/components/DuplicateLocationModal';
import { ServiceCheck, FeatureAlert } from '@/shared/components';
import { useAlertContext } from '@/contexts/alert.context';

import { InspectionFormData, useRequestInspection } from './useRequestInspection';

export default function RequestInspection() {
  const navigate = useNavigate();
  const { notify } = useAlertContext();
  const {
    items,
    proponents,
    attachments,
    itemColumns,
    editingItem,
    isSubmitting,
    isItemModalOpen,
    editingProponent,
    proponentColumns,
    editingAttachment,
    attachmentColumns,
    isProponentModalOpen,
    isAttachmentModalOpen,
    // Duplicação de localização
    isDuplicateModalOpen,
    duplicateInspections,
    pendingItemData,
    handleSubmit,
    handleNewItem,
    handleSubmitItem,
    handleNewProponent,
    handleNewAttachment,
    handleCloseItemModal,
    handleSubmitProponent,
    handleSubmitAttachment,
    handleCloseProponentModal,
    handleCloseAttachmentModal,
    // Duplicação de localização
    handleDuplicateConfirm,
    handleDuplicateCancel,
    handleCloseDuplicateModal,
  } = useRequestInspection();

  const finish = (values: InspectionFormData)=>{
   if (proponents.length < 1) {
    notify({ message: 'Adicione pelo menos um proponente', type: 'error' });
    return
   }
   if (items.length < 1) {
    notify({ message: 'Adicione pelo menos um item', type: 'error' });  
    return
   }
    handleSubmit(values);
  }
  
  return (
    <ProtectedRouteContainer
      pageHeader={
        <PageHeader
          title="Solicitar Inspeção"
          subtitle="Preencha os campos abaixo para solicitar uma nova inspeção."
        />
      }
    >
      <FeatureAlert services={['inspection', 'registration']} />
      <ServiceCheck
        services={['inspection', 'registration']}
        featureName="Solicitar Inspecao"
      >
      <Form name="basic" layout="vertical" onFinish={finish} className="w-full space-y-8">
        <Card className="shadow-sm">
          <InspectionForm />
        </Card>

        <TableCard
          title="Proponentes"
          dataSource={proponents}
          columns={proponentColumns}
          buttonTitle="Novo Proponente"
          handleAddNew={handleNewProponent}
          emptyText="Nenhum proponente registrado."
        />

        <TableCard
          title="Itens"
          dataSource={items}
          columns={itemColumns}
          buttonTitle="Novo Item"
          handleAddNew={handleNewItem}
          emptyText="Nenhum item registrado."
        />

        <TableCard
          title="Anexos"
          dataSource={attachments}
          buttonTitle="Novo Anexo"
          columns={attachmentColumns}
          handleAddNew={handleNewAttachment}
          emptyText="Nenhum anexo adicionado."
        />

        <RegisterFormButtons onClickCancel={() => navigate(-1)} loading={isSubmitting} />
      </Form>

      <ProponentModal
        open={isProponentModalOpen}
        onClose={handleCloseProponentModal}
        onSubmit={handleSubmitProponent}
        initialData={
          editingProponent
            ? {
                id: editingProponent.id,
                email: editingProponent.email,
                phones: editingProponent.phones,
                document: editingProponent.document,
                name: editingProponent.proponentName,
              }
            : undefined
        }
      />

      <ItemModal
        open={isItemModalOpen}
        onClose={handleCloseItemModal}
        onSubmit={handleSubmitItem}
        initialData={editingItem?.rawData}
      />

      <AttachmentModal
        open={isAttachmentModalOpen}
        onClose={handleCloseAttachmentModal}
        onSubmit={handleSubmitAttachment}
        initialData={
          editingAttachment
            ? {
                id: editingAttachment.id,
                fileName: editingAttachment.name,
                fileType: editingAttachment.type,
                description: editingAttachment.description,
                fileSize: parseInt(editingAttachment.size.replace(/[^0-9]/g, '')) || 0,
              }
            : undefined
        }
      />

      <DuplicateLocationModal
        open={isDuplicateModalOpen}
        onClose={handleCloseDuplicateModal}
        onConfirm={handleDuplicateConfirm}
        onCancel={handleDuplicateCancel}
        duplicateInspections={duplicateInspections}
        currentAddress={
          pendingItemData
            ? `${pendingItemData.street}, ${pendingItemData.number} - ${pendingItemData.district}, ${pendingItemData.city}/${pendingItemData.state}`
            : ''
        }
      />
      </ServiceCheck>
    </ProtectedRouteContainer>
  );
}
